import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { tryT, type MessageKey } from '@reos/i18n';
import { currentMembership } from '@reos/today-metrics';
import { useT } from '../lib/i18n.js';
import { formatDate } from '../lib/properties.js';
import { useSession } from '../stores/session.js';
import { listCatalog, listEntitlements, type CatalogItem } from '../lib/orders-api.js';
import { baseCodeOf, YEARLY_SUFFIX, yearlyMonthsCharged } from '../lib/plan-periods.js';

/**
 * Krok NAP11 (A25) — NASTAVENI → TARIF.
 *
 * Co kancelar o svem tarifu videla pred tímto krokem: nic. `GET /v1/entitlements`
 * (krok D3/D7) naroky vracelo, Dnesek z nich bral jen pruh „clenstvi konci",
 * a tarif, jeho platnost ani to, co obsahuje, nebylo na zadne obrazovce.
 *
 * Odkud se bere co:
 *  - aktualni tarif a platnost ← `GET /v1/entitlements` (naroky `MEMBERSHIP`),
 *    obdobi vybira `currentMembership` z `@reos/today-metrics` (TENTYZ zdroj
 *    pravdy, ze ktereho pocita pruh na Dnesku - ne druhy vypocet),
 *  - nazev tarifu a co obsahuje ← `GET /v1/public/catalog` (cenik: kredity
 *    tarifu, limit uzivatelu, `planGrants`),
 *  - odkaz na zmenu            ← tok „Tarif a platba" UVNITR aplikace
 *    (`trial.payUrl` ze session; od QAFIX9 je to `/tarif`, ne cenik verejneho
 *    webu - ten vedl na `/aktivace`, tedy na registraci NOVE kancelare).
 *
 * Zadne pravidlo „plati jeste?" se tu nepocita znovu - o pouzitelnosti naroku
 * rozhoduje server (`usable`/`unusableReason`).
 */

/**
 * Krok CEN2 — PREPINAC MESICNE/ROCNE (zadani: „prepinac na webovem ceniku
 * i v Nastaveni -> Tarif"). Konvence kodu `<kod>_YEARLY` a vypocet „10 mesicu
 * z 12" zije od QAFIX9 v `lib/plan-periods.ts` — tutez konvenci pouziva
 * obrazovka „Tarif a platba" (`routes/tarif.tsx`).
 */
function planRow(code: string, catalog: readonly CatalogItem[]): CatalogItem | undefined {
  return catalog.find((row) => row.code === code);
}

function priceText(row: CatalogItem | undefined): string | null {
  if (row?.priceNet === undefined) return null;
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency: row.currency,
    maximumFractionDigits: 0,
  }).format(Number(row.priceNet));
}

/** Nazev z ceniku; bez katalogoveho klice se ukaze kod produktu, nikdy klic. */
function planName(code: string, catalog: readonly CatalogItem[]): string {
  const item = catalog.find((row) => row.code === code);
  return item === undefined ? code : (tryT(item.nameKey as MessageKey, { locale: 'cs' }) ?? code);
}

export function TariffCard(): React.JSX.Element {
  const t = useT();
  const trial = useSession((s) => s.trial);
  const entitlements = useQuery({
    queryKey: ['nastaveni', 'tarif', 'entitlements'],
    queryFn: () => listEntitlements(),
  });
  const catalog = useQuery({
    queryKey: ['nastaveni', 'tarif', 'catalog'],
    queryFn: () => listCatalog(),
  });

  const items = entitlements.data ?? [];
  const membership = currentMembership(items);
  const catalogItems = catalog.data ?? [];
  const [yearly, setYearly] = React.useState(false);
  /*
   * Krok CEN2: prepinac se vaze na TARIF, ktery kancelar ma (nebo na ten
   * nejnizsi prodavany, kdyz zadny nema) — jinak by nebylo co prepinat.
   */
  const baseCode =
    membership === null ? null : baseCodeOf(membership.sourceProductCode);
  const monthlyRow = baseCode === null ? undefined : planRow(baseCode, catalogItems);
  const yearlyRow =
    baseCode === null ? undefined : planRow(`${baseCode}${YEARLY_SUFFIX}`, catalogItems);
  const months = yearlyMonthsCharged(monthlyRow?.priceNet, yearlyRow?.priceNet);
  const shownPrice = priceText(yearly ? yearlyRow : monthlyRow);

  /**
   * Co tarif obsahuje: kredity zaplaceneho mesice (`planGrants` cte server
   * z ceniku a promita je do naroku), tedy pocty reportu, ktere jsou v cene.
   * Vypis je z NAROKU - ne z inzerovaneho textu, aby na obrazovce stalo to,
   * co kancelar skutecne ma.
   */
  const included = items.filter(
    (item) => item.kind === 'MEMBERSHIP' && item.unusableReason !== 'REVOKED',
  );

  return (
    <div
      className="rh-card"
      style={{ maxWidth: 640, padding: 16, marginBottom: 16 }}
      data-testid="nastaveni-tariff-card"
    >
      <h2 className="mb-1 text-base font-semibold">{t('webAdmin.settings.tariff.title')}</h2>

      {entitlements.isPending ? (
        <p className="text-sm text-gray-600">{t('webAdmin.settings.tariff.loading')}</p>
      ) : membership === null ? (
        <p className="mb-2 text-sm text-gray-600" data-testid="nastaveni-tariff-none">
          {t('webAdmin.settings.tariff.none')}
        </p>
      ) : (
        <>
          <p className="mb-1 text-sm font-medium" data-testid="nastaveni-tariff-plan">
            {planName(membership.sourceProductCode, catalogItems)}
          </p>
          <p className="mb-2 text-sm text-gray-600" data-testid="nastaveni-tariff-valid-until">
            {t('webAdmin.settings.tariff.validUntil', { date: formatDate(membership.expiresAt) })}
          </p>
          {/*
            Krok CEN2: prepinac se kresli JEN kdyz je rocni varianta v ceniku
            (dokud migrace nedobehne, neni co prepinat) — nabidka, ktera po
            kliknuti nema cenu, je horsi nez zadna.
          */}
          {yearlyRow === undefined ? null : (
            <div className="mb-2" data-testid="nastaveni-tariff-period">
              <button
                type="button"
                className="mr-2 text-sm underline"
                aria-pressed={!yearly}
                onClick={() => setYearly(false)}
                data-testid="nastaveni-tariff-period-monthly"
              >
                {t('webAdmin.settings.tariff.periodMonthly')}
              </button>
              <button
                type="button"
                className="text-sm underline"
                aria-pressed={yearly}
                onClick={() => setYearly(true)}
                data-testid="nastaveni-tariff-period-yearly"
              >
                {t('webAdmin.settings.tariff.periodYearly')}
              </button>
              {shownPrice === null ? null : (
                <p className="mt-1 text-sm text-gray-700" data-testid="nastaveni-tariff-price">
                  {t('webAdmin.settings.tariff.priceNet', { price: shownPrice })}
                </p>
              )}
              {yearly && months !== null ? (
                <p className="text-sm text-gray-600" data-testid="nastaveni-tariff-yearly-note">
                  {t('webAdmin.settings.tariff.periodYearlyNote', { months })}
                </p>
              ) : null}
            </div>
          )}
          <p className="mb-1 text-sm font-medium">{t('webAdmin.settings.tariff.includes')}</p>
          <ul className="mb-2 list-disc pl-5 text-sm" data-testid="nastaveni-tariff-includes">
            {included.map((item) => (
              <li key={item.id}>
                {t('webAdmin.settings.tariff.includedItem', {
                  product: planName(item.productCode, catalogItems),
                  remaining: item.remainingReports ?? 0,
                  total: item.totalReports ?? 0,
                })}
              </li>
            ))}
          </ul>
        </>
      )}

      {/*
        Krok QAFIX3 (nalez N06): do QAFIX3 slibovala karta „Předplatné
        zařídíte v ceníku" HOLYM TEXTEM a kancelar bez tarifu nemela kam
        kliknout (odkaz se kreslil jen jako „Změnit tarif"). Ted je to vzdy
        ODKAZ — `trial.payUrl` vede od QAFIX9 na tok „Tarif a platba" UVNITR
        aplikace (`/tarif`), tedy TENTYZ platebni tok jako „Zaplatit
        předplatné" (zadny druhy nakupni tok, viz `office-trial.ts`).
        Popisek se lisi podle toho, jestli uz tarif je:
        „Objednat" x „Změnit".
      */}
      {trial?.payUrl == null ? (
        <p className="text-sm text-gray-600" data-testid="nastaveni-tariff-order-unavailable">
          {t('webAdmin.settings.tariff.orderUnavailable')}
        </p>
      ) : (
        <a
          className="text-sm underline"
          href={trial.payUrl}
          rel="noreferrer"
          data-testid={membership === null ? 'nastaveni-tariff-order' : 'nastaveni-tariff-change'}
        >
          {membership === null
            ? t('webAdmin.settings.tariff.order')
            : t('webAdmin.settings.tariff.change')}
        </a>
      )}
      {/*
        Krok PRO1: pri ZAVRENEM checkoutu (`commerce.legal_approved === false`)
        rekne karta DOPREDU, ze platba jeste nejde — odkaz zustava (vede na
        `/tarif`, kde je tentyz text a neaktivni „Zaplatit"), takze makler
        nenarazi az na 409 `commerce/legal-not-approved`. TYZ klic jako karta
        „Kancelář" a `/tarif`: jedna hlaska o zavrene platbe, ne tri.
      */}
      {trial?.checkoutOpen === false ? (
        <p className="mt-2 text-sm text-gray-600" data-testid="nastaveni-tariff-checkout-closed">
          {t('webAdmin.settings.officeTrial.checkoutClosed')}
        </p>
      ) : null}
    </div>
  );
}
